import {
  Box, Checkbox, Fab, IconButton, List, ListItem, ListItemButton,
  ListItemIcon, ListItemText, Paper, Tooltip, Typography
} from "@mui/material";
import { Ingredient } from "../../models/Ingredient";
import AddIcon from '@mui/icons-material/Add';
import RemoveIcon from '@mui/icons-material/Remove';
import AddShoppingCartIcon from '@mui/icons-material/AddShoppingCart';
import { useTranslation } from "react-i18next";
import { useCallback, useMemo, useState } from "react";
import { createPortal } from "react-dom";
import AddIngredientsToShoppingListDialog from "./AddIngredientsToShoppingListDialog";

export type IngredientsPageProps = {
  basePortions: number;
  portions: number;
  setPortions: (updater: (p: number) => number) => void;
  ingredients: Ingredient[];
  checkedIngredients: number[];
  changeCheckedIngredients: any;
}

const IngredientsPage = (props: IngredientsPageProps) => {
  const { t } = useTranslation();
  const { portions, setPortions } = props;
  const [shoppingListDialogOpen, setShoppingListDialogOpen] = useState(false);

  const checkIngredient = useCallback((id: number) => {
    props.changeCheckedIngredients((checked: number[]) => {
      if (checked.includes(id)) return checked.filter(i => i !== id);
      return [...checked, id];
    });
  }, [props]);

  const factor = useMemo(() => portions / props.basePortions, [portions, props.basePortions]);

  const groupedIngredients = useMemo(() => {
    const map = new Map<string, Ingredient[]>();
    [...props.ingredients]
      .sort((a, b) => a.orderNumber - b.orderNumber)
      .forEach((ing) => {
        const key = ing.group ?? '';
        const col = map.get(key);
        if (!col) map.set(key, [ing]); else col.push(ing);
      });
    return map;
  }, [props.ingredients]);

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', px: 2 }}>
      {/* Portion stepper */}
      <Paper elevation={2} sx={{
        display: 'flex', alignItems: 'center', gap: 1,
        px: 2, py: 1, borderRadius: 99, mt: 1.5, mb: 0.5,
      }}>
        <IconButton size="small" onClick={() => setPortions(p => Math.max(1, p - 1))}>
          <RemoveIcon fontSize="small" />
        </IconButton>
        <Typography fontWeight={700} sx={{ minWidth: 60, textAlign: 'center' }}>
          {portions} {t('portions')}
        </Typography>
        <IconButton size="small" onClick={() => setPortions(p => p + 1)}>
          <AddIcon fontSize="small" />
        </IconButton>
      </Paper>

      {/* Ingredient list */}
      <Box sx={{ width: '100%', maxWidth: 600, overflowY: 'auto', overflowX: 'hidden' }}>
        <List>
          {Array.from(groupedIngredients.keys()).map((group) => (
            <li key={`section-${group}`} style={{ listStyle: 'none' }}>
              <ul style={{ padding: 0, margin: 0 }}>
                {group && (
                  <Box sx={{
                    px: 1, py: 0.5, mt: 1,
                    fontSize: '0.72rem', fontWeight: 700, letterSpacing: '0.1em',
                    textTransform: 'uppercase', color: 'primary.main',
                  }}>
                    {group}
                  </Box>
                )}
                {groupedIngredients.get(group)?.map((ingredient: Ingredient) => {
                  const amountStr = ingredient.amount != null
                    ? `${(factor * ingredient.amount)}${ingredient.unit ? ' ' + ingredient.unit : ''}`
                    : null;
                  const checked = props.checkedIngredients.includes(ingredient.id!);
                  return (
                    <ListItem disablePadding key={ingredient.id}>
                      <ListItemButton onClick={() => checkIngredient(ingredient.id!)} dense sx={{ borderRadius: 2, mb: 0.25 }}>
                        <ListItemIcon sx={{ minWidth: 36 }}>
                          <Checkbox
                            edge="start" checked={checked} tabIndex={-1} disableRipple size="small"
                            sx={{ '&.Mui-checked': { color: 'primary.main' } }}
                          />
                        </ListItemIcon>
                        <ListItemText
                          primary={ingredient.name}
                          secondary={amountStr}
                          primaryTypographyProps={{
                            fontWeight: 500,
                            sx: {
                              textDecoration: checked ? 'line-through' : 'none',
                              color: checked ? 'text.secondary' : 'text.primary',
                            },
                          }}
                          secondaryTypographyProps={{ fontWeight: 600, color: 'primary.main', fontSize: '0.8rem' }}
                        />
                      </ListItemButton>
                    </ListItem>
                  );
                })}
              </ul>
            </li>
          ))}
        </List>
      </Box>

      {/* Portalled to the body: this page renders inside PullToRefresh, whose
          transformed wrapper would otherwise become the containing block and
          make `position: fixed` scroll along with the content. Sits in the
          right-hand column directly above the recipe action speed dial (which
          opens sideways, so its actions never run into this button). */}
      {createPortal(
        <Tooltip title={t('addToShoppingList')} placement="left">
          <Fab
            color="primary"
            aria-label="add to shopping list"
            sx={{
              position: 'fixed',
              bottom: 152,
              right: 24,
              zIndex: (theme) => theme.zIndex.speedDial,
            }}
            onClick={() => setShoppingListDialogOpen(true)}
          >
            <AddShoppingCartIcon />
          </Fab>
        </Tooltip>,
        document.body
      )}

      <AddIngredientsToShoppingListDialog
        open={shoppingListDialogOpen}
        setOpen={setShoppingListDialogOpen}
        ingredients={props.ingredients}
        factor={factor}
      />
    </Box>
  );
};

export default IngredientsPage;
